import React, { useEffect, useRef, useState } from 'react';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { ChatMessage } from '@nxsys/types/models/AiChat';
import {
  StyledMessageListWrapper,
  StyledEmptyState,
  StyledSuggestionList,
  StyledSuggestionItem,
  StyledShowMoreButton,
} from './index.styled';
import MessageBubble from './MessageBubble';
import TicketProposalCard from './TicketProposalCard';
import type { SuggestionChip } from './ChatWindow';
import { LogoRenderer } from './LogoRenderer';
import type { LogoKey } from './LogoRenderer';
import type { TicketProposal } from '@nxsys/types/models/AiChat';

const VISIBLE_COUNT = 4;

type Props = {
  messages: ChatMessage[];
  isStreaming: boolean;
  activeToolCall: { name: string; label: string } | null;
  greeting: string;
  isGreetingStreaming?: boolean;
  suggestions: SuggestionChip[];
  selectedLogo: LogoKey;
  usePrimaryColor: boolean;
  ticketProposal: TicketProposal | null;
  isRaisingTicket: boolean;
  onRaiseTicket: (proposal: TicketProposal, attachments?: string[], notes?: string, captureScreen?: boolean) => void;
  onDismissTicket: () => void;
  onFillInput: (text: string) => void;
  onSend: (text: string) => void;
};

const MessageList: React.FC<Props> = ({ messages, isStreaming, activeToolCall, greeting, isGreetingStreaming, suggestions, selectedLogo, usePrimaryColor, ticketProposal, isRaisingTicket, onRaiseTicket, onDismissTicket, onFillInput, onSend }) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Auto-scroll only while the user is parked near the bottom. If they scroll up
  // to read mid-stream, pause auto-scroll until they return to the bottom.
  const stickToBottomRef = useRef(true);
  const prevLenRef = useRef(messages.length);
  const [showAll, setShowAll] = useState(false);

  const handleScroll = () => {
    const el = wrapperRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 120;
  };

  useEffect(() => {
    // A new message (new turn) re-sticks to the bottom; in-place streaming
    // updates respect whatever the user is currently doing.
    if (messages.length > prevLenRef.current) stickToBottomRef.current = true;
    prevLenRef.current = messages.length;
    if (stickToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [messages, isStreaming, ticketProposal]);

  if (messages.length === 0) {
    const hasMore = suggestions.length > VISIBLE_COUNT;

    return (
      <StyledMessageListWrapper>
        <StyledEmptyState>
          <div style={{ width: 32, height: 32 }}>
            <LogoRenderer logoKey={selectedLogo} size={32} usePrimaryColor={usePrimaryColor} />
          </div>
          {greeting && <p>{greeting}</p>}
          {!isGreetingStreaming && suggestions.length > 0 && (
            <>
              <StyledSuggestionList $expanded={showAll}>
                {suggestions.map((s, idx) => (
                  <StyledSuggestionItem
                    key={s.prompt}
                    $delay={idx}
                    onClick={() => s.promptTemplate ? onFillInput(s.promptTemplate) : onSend(s.prompt)}
                  >
                    <span className="suggestion-index">{idx + 1}</span>
                    {s.label}
                  </StyledSuggestionItem>
                ))}
              </StyledSuggestionList>
              {hasMore && (
                <StyledShowMoreButton onClick={() => setShowAll(!showAll)}>
                  {showAll ? (
                    <>Show less <UpOutlined style={{ fontSize: 10 }} /></>
                  ) : (
                    <>Show more <DownOutlined style={{ fontSize: 10 }} /></>
                  )}
                </StyledShowMoreButton>
              )}
            </>
          )}
        </StyledEmptyState>
      </StyledMessageListWrapper>
    );
  }

  const lastIndex = messages.length - 1;

  return (
    <StyledMessageListWrapper ref={wrapperRef} onScroll={handleScroll}>
      {messages.map((msg, idx) => {
        const prev = idx > 0 ? messages[idx - 1] : null;
        const next = idx < lastIndex ? messages[idx + 1] : null;
        const sameAsPrev = prev?.role === msg.role;
        const sameAsNext = next?.role === msg.role;

        let groupPosition: 'single' | 'first' | 'middle' | 'last' = 'single';
        if (sameAsPrev && sameAsNext) groupPosition = 'middle';
        else if (sameAsPrev) groupPosition = 'last';
        else if (sameAsNext) groupPosition = 'first';

        return (
          <MessageBubble
            key={msg.id}
            message={msg}
            isStreaming={isStreaming && idx === lastIndex && msg.role === 'assistant'}
            groupPosition={groupPosition}
            activeToolCall={isStreaming && idx === lastIndex && msg.role === 'assistant' ? activeToolCall : null}
          />
        );
      })}
      {ticketProposal && !isStreaming && (
        <TicketProposalCard
          proposal={ticketProposal}
          isRaising={isRaisingTicket}
          onConfirm={(files, notes, capture) => onRaiseTicket(ticketProposal, files, notes, capture)}
          onDismiss={onDismissTicket}
        />
      )}
      <div ref={bottomRef} />
    </StyledMessageListWrapper>
  );
};

export default MessageList;
